/**
 * @fileoverview Mock backend for session self-management and change-password.
 * @description Registers GET /identity/me, GET/DELETE /identity/sessions(/:id),
 * POST /identity/logout(-all), and POST /identity/password/change on the feature's shared mock
 * adapter (see auth-mock-adapter.ts). The caller is identified by decoding the bearer token
 * mock-token.utils.ts encoded at sign-in — see that file for why this differs from how a real JWT
 * would be read. Every endpoint here answers an unauthenticated caller the way the identity
 * backend's access-token guard does for every protected route: an envelope with 401 / appCode 1020
 * (SESSION_INVALID_ERROR). /identity/me answers with the backend's identity fields plus the
 * mock-only extras (tenantId, activeRoleLevel, bankingScope); role and privileges come from
 * capabilities-mock.service.ts. A no-op when VITE_ENABLE_MOCK_API is false, so switching to a real
 * backend is deleting the one import of this file in features/auth/index.ts.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { authMockAdapter } from './auth-mock-adapter'
import {
  buildMockErrorEnvelope,
  buildMockSuccessEnvelope,
  type MockErrorEnvelope,
} from './mock-envelope.utils'
import { extractUserIdFromMockToken, getBearerTokenFromConfig } from './mock-token.utils'
import { findMockUserById } from './mock-user-directory'
import {
  clearOtherSessions,
  getCurrentSession,
  getOtherSessions,
  revokeOtherSession,
} from './session-mock-store'

import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
} from '@/features/auth/services/change-password.service'
import type {
  CurrentPrincipal,
  LogoutAllResponse,
  SessionSummary,
} from '@/features/auth/services/session.service'
import type { AxiosRequestConfig } from 'axios'

const SESSION_INVALID_ERROR: [number, MockErrorEnvelope] = [
  401,
  buildMockErrorEnvelope(401, '1020', 'Authenticated session is invalid or expired.'),
]

interface LogoutResponse {
  loggedOut: boolean
}

/**
 * @description Resolves the calling mock user from a mocked request's bearer token.
 * @param {AxiosRequestConfig} config - The request config the mock adapter's handler receives.
 * @returns {MockUserRecord | null} The matching mock user, or null when the token is
 * missing/unrecognized.
 */
function getCallingUser(config: AxiosRequestConfig) {
  const token = getBearerTokenFromConfig(config)
  const userId = token ? extractUserIdFromMockToken(token) : null
  return userId ? (findMockUserById(userId) ?? null) : null
}

if (authMockAdapter) {
  authMockAdapter.onGet('/identity/me').reply((config) => {
    const user = getCallingUser(config)

    if (!user) {
      return SESSION_INVALID_ERROR
    }

    const now = Date.now()
    const response: CurrentPrincipal = {
      userId: user.id,
      email: user.email,
      displayName: user.name ?? '',
      status: 'ACTIVE',
      mustChangePassword: user.mustChangePassword,
      session: user.mustChangePassword
        ? null
        : {
            sessionId: getCurrentSession(user.id).sessionId,
            deviceId: `mock-device.${user.id}`,
            authenticationMethod: 'PASSWORD_MFA',
            mfaVerifiedAt: new Date(now).toISOString(),
            absoluteExpiresAt: new Date(now + 24 * 60 * 60 * 1000).toISOString(),
            idleExpiresAt: new Date(now + 30 * 60 * 1000).toISOString(),
          },
      // Not yet served by the real backend — see CurrentPrincipal's doc comment.
      tenantId: 'tenant-01',
      activeRoleLevel: 1000,
      // mock-users.json's `bankingScope.type` is a plain untyped string literal at the JSON level
      // (JSON has no const-assertion), so it widens to `string` on import — this cast restores the
      // real "branch" | "region" discriminated-union contract the two locked mock users' records
      // actually hold.
      ...(user.bankingScope && { bankingScope: user.bankingScope }),
    }

    return [200, buildMockSuccessEnvelope(response, 'Current user retrieved successfully')]
  })

  authMockAdapter.onGet('/identity/sessions').reply((config) => {
    const user = getCallingUser(config)

    if (!user) {
      return SESSION_INVALID_ERROR
    }

    const response: SessionSummary[] = [getCurrentSession(user.id), ...getOtherSessions(user.id)]
    return [200, response]
  })

  authMockAdapter.onDelete(/\/identity\/sessions\/.+/).reply((config) => {
    const user = getCallingUser(config)

    if (!user) {
      return SESSION_INVALID_ERROR
    }

    const sessionId = config.url?.split('/').pop()

    if (sessionId) {
      revokeOtherSession(user.id, sessionId)
    }

    return [204]
  })

  authMockAdapter.onPost('/identity/logout').reply((config) => {
    if (!getCallingUser(config)) {
      return SESSION_INVALID_ERROR
    }

    const response: LogoutResponse = { loggedOut: true }
    return [200, buildMockSuccessEnvelope(response, 'Logged out successfully')]
  })

  authMockAdapter.onPost('/identity/logout-all').reply((config) => {
    const user = getCallingUser(config)

    if (!user) {
      return SESSION_INVALID_ERROR
    }

    const revokedOtherCount = clearOtherSessions(user.id)
    const response: LogoutAllResponse = {
      revokedSessionCount: revokedOtherCount + 1,
      reauthenticationRequired: true,
    }
    return [200, response]
  })

  authMockAdapter.onPost('/identity/password/change').reply((config) => {
    const user = getCallingUser(config)

    if (!user) {
      return SESSION_INVALID_ERROR
    }

    const { currentPassword, newPassword, confirmPassword } = JSON.parse(
      config.data as string,
    ) as ChangePasswordRequest

    if (currentPassword !== user.password) {
      return [400, buildMockErrorEnvelope(400, '1037', 'Current password is incorrect.')]
    }

    if (newPassword !== confirmPassword) {
      return [400, buildMockErrorEnvelope(400, '0001', 'Validation failed.')]
    }

    clearOtherSessions(user.id)
    const response: ChangePasswordResponse = {
      passwordChanged: true,
      loginRequired: true,
      sessionsRevoked: true,
    }
    return [200, buildMockSuccessEnvelope(response, 'Password changed successfully')]
  })
}
